import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalBody,
    Button,
    Input,
    Label,
    FormGroup,
    Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
    getFedexCandidateForCallDetails,
    submitFedexExistingDriverForCallDetails,
} from "model/CallModel";

const fedexFormSchema = Yup.object().shape({
    fedexId: Yup.string().trim().required("Fedex Id is required"),
    contactorName: Yup.string().trim().required("Contactor Name is required"),
    terminalAddress: Yup.string().trim().required("Terminal Address is required"),
    reasonForLeaving: Yup.string().trim().required("Reason For Leaving is required"),
});

const emptyForm = () => ({
    fedexId: "",
    contactorName: "",
    terminalAddress: "",
    reasonForLeaving: "",
});

const normalizeFedexPayload = (raw) => {
    const d = raw?.data ?? raw;
    if (!d || typeof d !== "object") return emptyForm();
    return {
        fedexId: String(d.fedexId ?? d.fedExId ?? "").trim(),
        contactorName: String(
            d.contactorName ?? d.contractorName ?? d.contactName ?? ""
        ).trim(),
        terminalAddress: String(d.terminalAddress ?? "").trim(),
        reasonForLeaving: String(d.reasonForLeaving ?? "").trim(),
    };
};

const hasMeaningfulFedexForm = (form) =>
    Boolean(
        form.fedexId ||
            form.contactorName ||
            form.terminalAddress ||
            form.reasonForLeaving
    );

const isSaveSuccess = (res) =>
    res?.success || res?.isSuccess || res?.status === "success" || res?.status === true;

const FedexExistingDriverModal = ({
    isOpen,
    toggle,
    candidateId,
    candidateClientId,
    onClose,
    setIsExistingFedexDriver
}) => {
    const [formData, setFormData] = useState(emptyForm());
    const [errors, setErrors] = useState({});
    const [formKey, setFormKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hadExistingRowsOnOpen, setHadExistingRowsOnOpen] = useState(false);

    const closeModal = (saved = false) => {
        onClose?.(saved, hadExistingRowsOnOpen);
        toggle();
    };

    const loadFedexCandidate = async () => {
        if (!candidateId) return;
        setLoading(true);
        try {
            const response = await getFedexCandidateForCallDetails({ candidateId });
            const mapped = normalizeFedexPayload(response);
            setFormData(mapped);
            setHadExistingRowsOnOpen(hasMeaningfulFedexForm(mapped));
            setFormKey((k) => k + 1);
        } catch {
            setFormData(emptyForm());
            setHadExistingRowsOnOpen(false);
            setFormKey((k) => k + 1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen || !candidateId) return;
        setErrors({});
        loadFedexCandidate();
    }, [isOpen, candidateId]);

    const handleChange = async (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (!fedexFormSchema.fields[name]) return;
        try {
            await Yup.reach(fedexFormSchema, name).validate(value);
            setErrors((prev) => ({ ...prev, [name]: "" }));
        } catch (err) {
            setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const clientId = String(candidateClientId ?? "").trim();
        if (!clientId) {
            toast.error("Client is required to save");
            return;
        }

        try {
            await fedexFormSchema.validate(formData, { abortEarly: false });
            setErrors({});
        } catch (err) {
            const next = {};
            err.inner.forEach((error) => {
                next[error.path] = error.message;
            });
            setErrors(next);
            return;
        }

        const payload = {
            candidateClientId: clientId,
            fedexId: String(formData.fedexId ?? "").trim(),
            contactorName: String(formData.contactorName ?? "").trim(),
            terminalAddress: String(formData.terminalAddress ?? "").trim(),
            reasonForLeaving: String(formData.reasonForLeaving ?? "").trim(),
        };

        setSaving(true);
        try {
            const response = await submitFedexExistingDriverForCallDetails(payload);
            if (isSaveSuccess(response)) {
                toast.success(response?.message || "Saved successfully");
                setIsExistingFedexDriver(true)
                closeModal(true);
            } else {
                toast.error(response?.message || "Save failed");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={() => closeModal(false)}
            backdrop="static"
            className="modal-full-height"
            centered
        >
            <ModalBody
                key={formKey}
                className="p-4"
                style={{ maxHeight: "95vh", overflowY: "auto" }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 text-uppercase">Fedex Existing Driver</h5>
                    <Button close onClick={() => closeModal(false)} />
                </div>
                <hr />

                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label>
                            Fedex Id <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            name="fedexId"
                            value={formData.fedexId}
                            onChange={handleChange}
                            disabled={loading}
                        />
                        {errors.fedexId && (
                            <small className="text-danger">{errors.fedexId}</small>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>
                            Contractor Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            name="contactorName"
                            value={formData.contactorName}
                            onChange={handleChange}
                            disabled={loading}
                        />
                        {errors.contactorName && (
                            <small className="text-danger">{errors.contactorName}</small>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>
                            Terminal Address <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            name="terminalAddress"
                            value={formData.terminalAddress}
                            onChange={handleChange}
                            disabled={loading}
                        />
                        {errors.terminalAddress && (
                            <small className="text-danger">{errors.terminalAddress}</small>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>
                            Reason For Leaving <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            name="reasonForLeaving"
                            value={formData.reasonForLeaving}
                            onChange={handleChange}
                            disabled={loading}
                        />
                        {errors.reasonForLeaving && (
                            <small className="text-danger">{errors.reasonForLeaving}</small>
                        )}
                    </FormGroup>

                    <div className="text-end mt-3">
                        <Button color="primary" type="submit" disabled={loading || saving}>
                            {/* <i className="fa fa-save me-1" /> */}
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default FedexExistingDriverModal;
